/**
 * BuchhaltungVorbereitungPage — Buchungsvorschläge, BWA, Auswertungen
 *
 * 3-Tab Layout:
 * 1. Buchungsvorschläge — KI-gestützte Buchungsvorschläge prüfen/akzeptieren
 * 2. BWA — Betriebswirtschaftliche Auswertung
 * 3. Auswertungen — KPIs, Umsatz nach Geschäftsbereich, Monatsübersicht
 */

import { useState, useMemo } from 'react';
import { useBuchungsvorschlaege, useBWA, useEingangsbelege, updateBuchungsvorschlag } from '@/hooks/useBuchhaltung';
import { useRechnungen } from '@/hooks/useSupabase';
import type { FirmaId, BuchungsvorschlagStatus } from '@/lib/database.types';
import {
  BookOpen, BarChart3, PieChart, Brain, Check, X, ArrowRight,
  Loader2, AlertTriangle, Building2, Calendar, TrendingUp,
  TrendingDown,
} from 'lucide-react';
import { toast } from 'sonner';
import { localIsoDate } from '@/lib/date-utils';

// ============================================================
// HELPERS
// ============================================================

function formatEuro(n: number): string {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(n);
}

function formatDate(d: string | null): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  vorschlag: { label: 'Vorschlag', color: 'bg-blue-50 text-blue-600' },
  akzeptiert: { label: 'Akzeptiert', color: 'bg-emerald-50 text-emerald-600' },
  angepasst: { label: 'Angepasst', color: 'bg-amber-50 text-amber-600' },
  abgelehnt: { label: 'Abgelehnt', color: 'bg-red-50 text-red-600' },
  gebucht: { label: 'Gebucht', color: 'bg-gray-100 text-gray-500' },
};

function StatusBadge({ status }: { status: string }) {
  const cfg = STATUS_CONFIG[status] || { label: status, color: 'bg-gray-100 text-gray-500' };
  return <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${cfg.color}`}>{cfg.label}</span>;
}

// ============================================================
// TAB: BUCHUNGSVORSCHLÄGE
// ============================================================

function BuchungsvorschlaegeTab({ firma }: { firma: FirmaId }) {
  const { data: vorschlaegeRaw, loading, refetch } = useBuchungsvorschlaege(firma);
  const { data: belegeRaw } = useEingangsbelege(firma);
  const vorschlaege = vorschlaegeRaw || [];
  const belege = belegeRaw || [];

  // Belege ohne Buchungsvorschlag
  const belegeMitVorschlag = new Set(vorschlaege.map(v => v.eingangsbeleg_id).filter(Boolean));
  const belegeOhneVorschlag = belege.filter(b => !belegeMitVorschlag.has(b.id) && !['abgelehnt', 'ueberwiesen'].includes(b.status));

  const handleAction = async (id: string, status: BuchungsvorschlagStatus) => {
    try {
      await updateBuchungsvorschlag(id, { status });
      toast.success(`Buchungsvorschlag ${STATUS_CONFIG[status]?.label || status}`);
      refetch();
    } catch (err: any) {
      toast.error('Fehler: ' + (err.message || 'Unbekannt'));
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>;
  }

  return (
    <div className="space-y-4">
      {/* Warning: Belege ohne Vorschlag */}
      {belegeOhneVorschlag.length > 0 && (
        <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <div className="text-sm font-medium text-amber-900">{belegeOhneVorschlag.length} Belege ohne Buchungsvorschlag</div>
            <div className="text-xs text-amber-700 mt-0.5">
              Diese Eingangsbelege haben noch keinen automatischen Buchungsvorschlag erhalten.
            </div>
          </div>
        </div>
      )}

      {vorschlaege.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <BookOpen className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <div className="text-sm">Keine Buchungsvorschläge vorhanden</div>
        </div>
      ) : (
        <div className="space-y-2">
          {vorschlaege.map(v => (
            <div key={v.id} className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-sm transition-shadow">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <StatusBadge status={v.status} />
                    {v.ki_generiert && (
                      <span className="flex items-center gap-1 text-[10px] font-medium text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded-full">
                        <Brain className="w-3 h-3" /> KI
                      </span>
                    )}
                  </div>
                  <div className="text-sm font-medium text-gray-900 mt-1">{v.buchungstext || 'Buchung'}</div>
                  <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                    <span>Soll: <span className="font-mono font-medium text-gray-700">{v.soll_konto}</span></span>
                    <ArrowRight className="w-3 h-3" />
                    <span>Haben: <span className="font-mono font-medium text-gray-700">{v.haben_konto}</span></span>
                    <span className="font-semibold text-gray-900">{formatEuro(v.betrag)}</span>
                  </div>
                  {v.eingangsbelege && (
                    <div className="text-[10px] text-gray-400 mt-1">
                      Beleg: {v.eingangsbelege.absender_firma || v.eingangsbelege.absender_name || '—'} — {formatDate(v.eingangsbelege.belegdatum)}
                    </div>
                  )}
                </div>
                {v.status === 'vorschlag' && (
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button onClick={() => handleAction(v.id, 'akzeptiert')}
                      className="p-1.5 rounded-lg bg-emerald-50 text-emerald-600 hover:bg-emerald-100 transition-colors"
                      title="Akzeptieren">
                      <Check className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleAction(v.id, 'abgelehnt')}
                      className="p-1.5 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 transition-colors"
                      title="Ablehnen">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
                {v.status === 'akzeptiert' && (
                  <button onClick={() => handleAction(v.id, 'gebucht')}
                    className="px-3 py-1.5 text-xs font-medium text-white bg-[#C41E3A] hover:bg-[#A3172F] rounded-lg transition-colors flex-shrink-0">
                    Buchen
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================
// TAB: BWA
// ============================================================

function BWATab({ firma }: { firma: FirmaId }) {
  const thisYear = new Date().getFullYear();
  const [von, setVon] = useState(`${thisYear}-01-01`);
  const [bis, setBis] = useState(localIsoDate(new Date()));
  const { data: bwa, loading } = useBWA(firma, von, bis);

  if (loading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>;
  }

  const ergebnis = bwa?.betriebsergebnis || 0;
  const ergebnisPositiv = ergebnis >= 0;

  return (
    <div className="space-y-6">
      {/* Zeitraum */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <Calendar className="w-4 h-4 text-gray-400" />
        <span className="text-sm text-gray-600">Zeitraum:</span>
        <input type="date" value={von} onChange={e => setVon(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20" />
        <span className="text-sm text-gray-400">bis</span>
        <input type="date" value={bis} onChange={e => setBis(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20" />
      </div>

      {/* BWA Report */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h3 className="text-sm font-bold text-gray-900 mb-4">Betriebswirtschaftliche Auswertung</h3>
        <div className="space-y-3">
          {/* Umsatzerlöse */}
          <div className="flex items-center justify-between py-2 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-emerald-500" />
              <span className="text-sm text-gray-700">Umsatzerlöse (netto)</span>
            </div>
            <span className="text-sm font-bold text-emerald-700">{formatEuro(bwa?.umsatzerloese || 0)}</span>
          </div>
          <div className="flex items-center justify-between py-2 border-b border-gray-100 pl-6">
            <span className="text-xs text-gray-500">Umsatzsteuer</span>
            <span className="text-xs text-gray-500">{formatEuro(bwa?.umsatzsteuer || 0)}</span>
          </div>
          <div className="flex items-center justify-between py-2 border-b border-gray-100 pl-6">
            <span className="text-xs text-gray-500">Rechnungen gesamt</span>
            <span className="text-xs text-gray-500">{bwa?.rechnungenGesamt || 0}</span>
          </div>

          {/* Aufwendungen */}
          <div className="flex items-center justify-between py-2 border-b border-gray-100 mt-2">
            <div className="flex items-center gap-2">
              <TrendingDown className="w-4 h-4 text-red-500" />
              <span className="text-sm text-gray-700">Aufwendungen (netto)</span>
            </div>
            <span className="text-sm font-bold text-red-600">-{formatEuro(bwa?.aufwendungen || 0)}</span>
          </div>
          <div className="flex items-center justify-between py-2 border-b border-gray-100 pl-6">
            <span className="text-xs text-gray-500">Vorsteuer</span>
            <span className="text-xs text-gray-500">{formatEuro(bwa?.vorsteuer || 0)}</span>
          </div>
          <div className="flex items-center justify-between py-2 border-b border-gray-100 pl-6">
            <span className="text-xs text-gray-500">Eingangsbelege gesamt</span>
            <span className="text-xs text-gray-500">{bwa?.belegeGesamt || 0}</span>
          </div>

          {/* Betriebsergebnis */}
          <div className={`flex items-center justify-between py-3 mt-2 rounded-lg px-3 ${ergebnisPositiv ? 'bg-emerald-50' : 'bg-red-50'}`}>
            <span className="text-sm font-bold text-gray-900">Betriebsergebnis</span>
            <span className={`text-lg font-bold ${ergebnisPositiv ? 'text-emerald-700' : 'text-red-700'}`}>
              {formatEuro(ergebnis)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// TAB: AUSWERTUNGEN
// ============================================================

function AuswertungenTab({ firma }: { firma: FirmaId }) {
  const { data: rechnungenRaw } = useRechnungen();
  const { data: belegeRaw } = useEingangsbelege(firma);
  const rechnungen = rechnungenRaw || [];
  const belege = belegeRaw || [];

  const stats = useMemo(() => {
    const now = new Date();
    const thisYear = now.getFullYear().toString();

    // Jahresumsatz
    const jahresUmsatz = rechnungen
      .filter(r => r.status === 'bezahlt' && r.bezahlt_am?.startsWith(thisYear))
      .reduce((s, r) => s + r.brutto_betrag, 0);

    // Jahresausgaben
    const jahresAusgaben = belege
      .filter(b => !['abgelehnt'].includes(b.status) && b.belegdatum?.startsWith(thisYear))
      .reduce((s, b) => s + (b.brutto_betrag || 0), 0);

    // Offene Rechnungen
    const offeneRechnungen = rechnungen.filter(r => !['bezahlt', 'storniert'].includes(r.status));

    // Umsatz nach Geschäftsbereich
    const umsatzNachBU: Record<string, number> = {};
    rechnungen
      .filter(r => r.status === 'bezahlt' && r.bezahlt_am?.startsWith(thisYear))
      .forEach(r => {
        const bu = r.business_unit || 'sonstige';
        umsatzNachBU[bu] = (umsatzNachBU[bu] || 0) + r.brutto_betrag;
      });

    // Monatsübersicht (letzte 6 Monate)
    const monate: { label: string; einnahmen: number; ausgaben: number }[] = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = d.toISOString().slice(0, 7);
      const label = d.toLocaleDateString('de-DE', { month: 'short' });
      const ein = rechnungen
        .filter(r => r.status === 'bezahlt' && r.bezahlt_am?.startsWith(key))
        .reduce((s, r) => s + r.brutto_betrag, 0);
      const aus = belege
        .filter(b => !['abgelehnt'].includes(b.status) && b.belegdatum?.startsWith(key))
        .reduce((s, b) => s + (b.brutto_betrag || 0), 0);
      monate.push({ label, einnahmen: ein, ausgaben: aus });
    }

    return { jahresUmsatz, jahresAusgaben, offeneRechnungen: offeneRechnungen.length, umsatzNachBU, monate };
  }, [rechnungen, belege]);

  const buLabels: Record<string, string> = {
    gutachten: 'Kfz Gutachten',
    mietwagen: 'Mietwagen',
    autoglas: 'Autoglas',
    werkstatt: 'Werkstatt',
    protex: 'Protex',
    sonstige: 'Sonstige',
  };

  const buColors: Record<string, string> = {
    gutachten: '#2563EB',
    mietwagen: '#D97706',
    autoglas: '#059669',
    werkstatt: '#DC2626',
    protex: '#7C3AED',
    sonstige: '#6B7280',
  };

  const maxBU = Math.max(...Object.values(stats.umsatzNachBU), 1);

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="text-xs text-gray-500 mb-1">Jahresumsatz {new Date().getFullYear()}</div>
          <div className="text-2xl font-bold text-emerald-700">{formatEuro(stats.jahresUmsatz)}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="text-xs text-gray-500 mb-1">Jahresausgaben {new Date().getFullYear()}</div>
          <div className="text-2xl font-bold text-red-600">{formatEuro(stats.jahresAusgaben)}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="text-xs text-gray-500 mb-1">Offene Rechnungen</div>
          <div className="text-2xl font-bold text-gray-900">{stats.offeneRechnungen}</div>
        </div>
      </div>

      {/* Umsatz nach Geschäftsbereich */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h3 className="text-sm font-bold text-gray-900 mb-4">Umsatz nach Geschäftsbereich</h3>
        <div className="space-y-3">
          {Object.entries(stats.umsatzNachBU)
            .sort(([, a], [, b]) => b - a)
            .map(([bu, betrag]) => (
              <div key={bu}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-gray-700">{buLabels[bu] || bu}</span>
                  <span className="text-xs font-bold text-gray-900">{formatEuro(betrag)}</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2">
                  <div
                    className="h-2 rounded-full transition-all duration-500"
                    style={{
                      width: `${Math.max((betrag / maxBU) * 100, 2)}%`,
                      backgroundColor: buColors[bu] || '#6B7280',
                    }}
                  />
                </div>
              </div>
            ))}
        </div>
      </div>

      {/* Monatsübersicht */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h3 className="text-sm font-bold text-gray-900 mb-4">Monatsübersicht (letzte 6 Monate)</h3>
        <div className="space-y-2">
          {stats.monate.map(m => (
            <div key={m.label} className="flex items-center gap-3 py-2 border-b border-gray-50">
              <span className="text-xs font-medium text-gray-500 w-12">{m.label}</span>
              <div className="flex-1 flex items-center gap-2">
                <span className="text-xs text-emerald-600 font-medium w-24 text-right">+{formatEuro(m.einnahmen)}</span>
                <span className="text-xs text-red-500 font-medium w-24 text-right">-{formatEuro(m.ausgaben)}</span>
                <span className={`text-xs font-bold w-24 text-right ${m.einnahmen - m.ausgaben >= 0 ? 'text-gray-900' : 'text-red-700'}`}>
                  = {formatEuro(m.einnahmen - m.ausgaben)}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// MAIN COMPONENT
// ============================================================

type TabId = 'vorschlaege' | 'bwa' | 'auswertungen';

export default function BuchhaltungVorbereitungPage() {
  const [activeTab, setActiveTab] = useState<TabId>('vorschlaege');
  const [firma, setFirma] = useState<FirmaId>('gutachter_koop');

  const tabs: { id: TabId; label: string; icon: typeof BookOpen }[] = [
    { id: 'vorschlaege', label: 'Buchungsvorschläge', icon: BookOpen },
    { id: 'bwa', label: 'BWA', icon: BarChart3 },
    { id: 'auswertungen', label: 'Auswertungen', icon: PieChart },
  ];

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Buchhaltungsvorbereitung</h1>
          <p className="text-sm text-gray-500 mt-1">Buchungsvorschläge, BWA und Auswertungen</p>
        </div>
        <div className="flex items-center gap-2">
          <Building2 className="w-4 h-4 text-gray-400" />
          <select value={firma} onChange={e => setFirma(e.target.value as FirmaId)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20">
            <option value="gutachter_koop">Gutachter Koop</option>
            <option value="lcs_gmbh">LCS GmbH</option>
          </select>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-gray-200 mb-6">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
              activeTab === tab.id
                ? 'border-[#C41E3A] text-[#C41E3A]'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'vorschlaege' && <BuchungsvorschlaegeTab firma={firma} />}
      {activeTab === 'bwa' && <BWATab firma={firma} />}
      {activeTab === 'auswertungen' && <AuswertungenTab firma={firma} />}
    </div>
  );
}
